import { zodResolver } from "@hookform/resolvers/zod";
import { backupCodeSchema, otpCodeSchema } from "@purlink/shared/auth";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { KeyRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AuthLayout, CodeInput, FormError } from "~/components/auth/auth-layout";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { toast } from "~/components/ui/toaster";
import { AuthError, authApi, errorMessage, refreshMe } from "~/lib/auth";

export const Route = createFileRoute("/_auth/two-factor")({
  validateSearch: z.object({
    method: z.enum(["totp", "otp"]).catch("totp"),
    redirect: z.string().startsWith("/").optional().catch(undefined),
  }),
  component: TwoFactorPage,
});

type Mode = "totp" | "otp" | "backup";

function TwoFactorPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>(search.method);
  const [sending, setSending] = useState(false);
  const sentOnce = useRef(false);

  const schema = z.object({ code: mode === "backup" ? backupCodeSchema : otpCodeSchema });
  const form = useForm<{ code: string }>({
    resolver: zodResolver(schema),
    defaultValues: { code: "" },
  });
  const { errors, isSubmitting } = form.formState;

  const sendCode = async () => {
    setSending(true);
    try {
      await authApi.sendOtp();
      toast.success("We've emailed you a sign-in code.");
    } catch (error) {
      if (error instanceof AuthError && error.status === 401) {
        await navigate({ to: "/login" });
        return;
      }
      toast.error(errorMessage(error));
    } finally {
      setSending(false);
    }
  };

  // Email users get their code as soon as the page opens.
  useEffect(() => {
    if (search.method === "otp" && !sentOnce.current) {
      sentOnce.current = true;
      void sendCode();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- send once on mount
  }, []);

  const onSubmit = form.handleSubmit(async ({ code }) => {
    try {
      if (mode === "totp") await authApi.verifyTotp(code.trim());
      else if (mode === "otp") await authApi.verifyOtp(code.trim());
      else await authApi.verifyBackupCode(code.trim());
      const me = await refreshMe(queryClient);
      await navigate({
        to: me?.onboarded ? (search.redirect ?? "/dashboard") : "/onboarding/security",
      });
    } catch (error) {
      if (error instanceof AuthError && error.code === "INVALID_TWO_FACTOR_COOKIE") {
        await navigate({ to: "/login" });
        return;
      }
      form.setError("code", {
        message:
          error instanceof AuthError && error.status === 401
            ? mode === "backup"
              ? "That backup code isn't valid."
              : "That code didn't match. Try again."
            : errorMessage(error),
      });
      form.resetField("code", { keepError: true });
    }
  });

  const descriptions: Record<Mode, string> = {
    totp: "Enter the 6-digit code from your authenticator app.",
    otp: "Enter the 6-digit code we emailed you. It expires in 10 minutes.",
    backup: "Enter one of the backup codes you saved when you set up two-step sign-in.",
  };

  return (
    <AuthLayout
      title="Two-step sign-in"
      description={descriptions[mode]}
      footer={
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={mode === "backup" ? "Backup code" : "Code"} error={errors.code?.message}>
          {mode === "backup" ? (
            <Input autoComplete="off" autoFocus spellCheck={false} {...form.register("code")} />
          ) : (
            <CodeInput autoFocus {...form.register("code")} />
          )}
        </Field>
        <FormError message={errors.root?.server?.message} />
        <Button type="submit" size="lg" loading={isSubmitting}>
          Verify
        </Button>
      </form>
      <div className="flex flex-col items-center gap-2 text-small">
        {mode === "otp" && (
          <Button variant="link" size="sm" onClick={sendCode} loading={sending}>
            Send a new code
          </Button>
        )}
        {mode !== "backup" ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setMode("backup");
              form.reset({ code: "" });
            }}
          >
            <KeyRound /> Use a backup code
          </Button>
        ) : (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setMode(search.method);
              form.reset({ code: "" });
            }}
          >
            Use my {search.method === "totp" ? "authenticator app" : "email code"} instead
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
